import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import isEmpty from "lodash/isEmpty";
import { motion } from "motion/react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router";

import { useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import Swal from "sweetalert2";
import { IMAGES } from "../../assets/images";
import FullScreenLoader from "../../components/fullScreenLoader";
import Header from "../../components/header";
import IzunTextField from "../../components/izunTextField";
import { CONSTANTS, RoutesType } from "../../constants";
import { fetcher } from "../../network";
import { API } from "../../network/api";
import { fetchDoctors, patientsQueryKey } from "../../network/globalFetches";
import { Patient } from "../../types";
import { AddPatientResponse, EditPatientResponse } from "../../types/responses";
import Drawer from "../home/components/drawer";

type FormInputs = {
  name: string;
  allergies: string;
  doctorId: string;
  enterDateToHome: Date;
  exitDateFromHome?: Date;
};

const addPatientMutationFn = async (
  patient: Omit<Patient, "_id" | "doctor"> & { doctorId?: string }
) => {
  const { data } = await fetcher().post<AddPatientResponse>(
    API.POST.ADD_PATIENT,
    {
      ...patient,
    }
  );

  return data;
};

const editPatientMutation = async ({
  patientId,
  ...body
}: Partial<Omit<Patient, "_id">> & {
  doctorId?: string;
  patientId: string;
}) => {
  const { data } = await fetcher().put<EditPatientResponse>(
    API.PUT.EDIT_PATIENT(patientId),
    {
      ...body,
    }
  );

  return data;
};

type AddPatientPageProps = {
  from?: RoutesType;
};

const AddPatientPage = ({ from = "/" }: AddPatientPageProps) => {
  const navigate = useNavigate();
  const { state } = useLocation();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const patient = state?.patient as Patient | undefined;

  const { data: doctors, isLoading: isLoadingDoctorsQuery } = useQuery({
    queryKey: fetchDoctors.queryKey,
    queryFn: fetchDoctors.fetch,
    staleTime: Infinity,
  });

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormInputs>({
    defaultValues: patient && {
      allergies: patient.allergies,
      doctorId: patient.doctorId,
      name: patient.name,
      enterDateToHome: patient.enterDateToHome,
      exitDateFromHome: patient.exitDateFromHome,
    },
  });

  const queryClient = useQueryClient();

  const toggleDrawer = () => {
    setIsDrawerOpen((prev) => !prev);
  };

  const { mutate: addPatient, isPending } = useMutation<
    AddPatientResponse,
    Error,
    Omit<Patient, "_id"> & { doctorId?: string }
  >({
    mutationFn: addPatientMutationFn,
    onError: () => {
      toast.error(
        "תקלת מערכת - הדייר החליט לא להישמר,אולי תצליח לשכנע אותו שוב? 🤔"
      );
    },
    onSuccess: ({ patient }) => {
      queryClient.setQueryData<Patient[]>(patientsQueryKey, (oldData) => {
        if (!oldData) return [patient];
        oldData.unshift(patient);
        return oldData;
      });

      Swal.fire({
        position: "center",
        icon: "success",
        title: `הדייר ${patient.name} נשמר בהצלחה`,
        showConfirmButton: true,
        confirmButtonText: "להוספת כדורים",
        confirmButtonColor: "#C64192",
        showCancelButton: true,
        cancelButtonText: "הוסף דייר נוסף",
        cancelButtonColor: "#144082",
      }).then(({ isConfirmed }) => {
        if (isConfirmed) {
          navigate(CONSTANTS.ROUTES.pillsManager, {
            replace: true,
            state: { patientId: patient._id },
          });
        }
      });
      reset();
    },
  });

  const { mutate: editPatient, isPending: isLoadingEdit } = useMutation<
    EditPatientResponse,
    Error,
    Partial<Omit<Patient, "_id">> & { doctorId?: string; patientId: string }
  >({
    mutationFn: editPatientMutation,
    onError: () => {
      toast.error(
        "תקלת מערכת - הדייר החליט שהוא נשאר עם הפרטים הקודמים, אולי תצליח לשכנע אותו שוב? 🤔"
      );
    },
    onSuccess: ({ updatedPatient }) => {
      queryClient.setQueryData<Patient[]>(patientsQueryKey, (oldData) => {
        if (!oldData) return [updatedPatient];
        oldData.map((data) => {
          if (data._id === patient?._id) {
            return {
              ...updatedPatient,
            };
          }
          return data;
        });
        return oldData;
      });

      toast.success("הפרטים נערכו בהצלחה 🧚🏻‍♂️");

      navigate(from || CONSTANTS.ROUTES.home, {
        state: {
          from: CONSTANTS.ROUTES.addPatients,
        },
      });

      reset();
    },
  });

  useEffect(() => {
    if (doctors) {
      if (isEmpty(doctors)) {
        Swal.fire({
          position: "center",
          icon: "warning",
          title: "שמנו לב שאין רופאים במערכת",
          text: "כדאי להוסיף רופא לפני יצירת הדייר",
          showConfirmButton: true,
          confirmButtonText: "למסך ניהול הרופאים",
          confirmButtonColor: "#C64192",
          showCancelButton: true,
          cancelButtonText: "המשך בהוספת דייר",
          cancelButtonColor: "#144082",
        }).then(({ isConfirmed }) => {
          if (isConfirmed) {
            navigate(CONSTANTS.ROUTES.doctors, {
              state: { from: "" },
            });
          }
        });
      }
    }
  }, [doctors]);

  const handleSave: SubmitHandler<FormInputs> = (data) => {
    if (patient?._id) {
      editPatient({ ...data, patientId: patient._id });
    } else {
      addPatient({
        ...data,
        timeToTakePills: [],
        defaultImg: "",
        pills: [],
        todayTakenPillsHistory: [],
      });
    }
  };

  return (
    <div className="container mx-auto relative h-screen">
      <Header>
        <Header.Time />
        <Header.Title title={`${patient ? "עריכת דייר" : "הוספת דייר"}`} />
        <Header.Menu toggleDrawer={toggleDrawer} />
      </Header>
      <form className="mt-6 z-10 relative" onSubmit={handleSubmit(handleSave)}>
        <div className="flex flex-col w-2/3 sm:w-1/3 mx-auto gap-6">
          <div className="w-full">
            <div className="relative" dir="rtl">
              <IzunTextField
                registerRetrun={register("name", { required: "שדה חובה" })}
                error={errors.name}
                label="שם הדייר"
                type="text"
                required
              />
            </div>
            {errors.name && (
              <motion.p
                animate={{ opacity: 1 }}
                className="text-red-500 text-sm">
                {errors.name.message}
              </motion.p>
            )}
          </div>

          <div className="w-full">
            <div className="relative" dir="rtl">
              <IzunTextField
                registerRetrun={register("enterDateToHome", {
                  required: "שדה חובה",
                })}
                error={errors.enterDateToHome}
                label="תאריך כניסה&nbsp;&nbsp;&nbsp;&nbsp;"
                type="date"
                required
                defaultValue={dayjs().format("YYYY-MM-DD")}
              />
            </div>
            {errors.enterDateToHome && (
              <motion.p
                animate={{ opacity: 1 }}
                className="text-red-500 text-sm">
                {errors.enterDateToHome?.message}
              </motion.p>
            )}
          </div>
          <div className="w-full">
            <div className="relative" dir="rtl">
              <IzunTextField
                registerRetrun={register("exitDateFromHome")}
                error={errors.exitDateFromHome}
                label="תאריך יציאה&nbsp;&nbsp;&nbsp;&nbsp;"
                type="date"
              />
            </div>
            {errors.exitDateFromHome && (
              <motion.p
                animate={{ opacity: 1 }}
                className="text-red-500 text-sm">
                {errors.exitDateFromHome?.message}
              </motion.p>
            )}
          </div>

          <div className="w-full">
            {doctors && !isEmpty(doctors) && (
              <div className="relative">
                <FormControl fullWidth error={!!errors.doctorId}>
                  <InputLabel id="doctor-select-label">
                    בחר רופא מטפל
                  </InputLabel>
                  <Controller
                    name="doctorId"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId="doctor-select-label"
                        label="בחר רופא מטפל">
                        <MenuItem value="">בחר רופא מטפל</MenuItem>
                        {/* Placeholder option */}
                        {doctors.map((doctor) => (
                          <MenuItem key={doctor._id} value={doctor._id}>
                            {doctor.name}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  {errors.doctorId && (
                    <FormHelperText>יש לבחור רופא מטפל</FormHelperText>
                  )}
                </FormControl>
              </div>
            )}
          </div>
          <textarea
            {...register("allergies")}
            name="allergies"
            placeholder="רגישויות"
            className="border rounded outline-none px-4 py-2 min-h-32"
          />
        </div>
        <div className="flex justify-center my-8">
          <button
            type="submit"
            className="bg-[#144082] text-white rounded py-2 px-8 hover:scale-110">
            {patient?._id ? "עדכון" : "שמירה"}
          </button>
        </div>
      </form>

      <div className="w-52 md:w-auto absolute bottom-0 left-0 mb-6 ml-6 z-0">
        <img
          src={IMAGES.ADD_PATIENT}
          alt="תמונת רקע"
          className="object-contain"
        />
      </div>
      {isDrawerOpen && <Drawer toggleDrawer={toggleDrawer} />}
      <Toaster position="bottom-center" />
      {(isPending || isLoadingEdit || isLoadingDoctorsQuery) && (
        <FullScreenLoader />
      )}
    </div>
  );
};

export default AddPatientPage;
